import React, { useContext, useEffect, useRef, useState } from "react";
import "./PaymentMethod.css";
import InputField from "../../components/InputField/InputField";
import { TbAlertTriangle, TbArrowLeft, TbCreditCard } from "react-icons/tb";
import Button from "../../components/Button/Button";
import CreditCard from "../../components/CreditCard/CreditCard";
import { useLocation, useNavigate } from "react-router-dom";
import Services from "../../services/Services";
import SuccessModal from "../../components/SuccessModal/SuccessModal";
import ErrorModal from "../../components/ErrorModal/ErrorModal";
import { MallContext } from "../../MallContext";
import { useLanguage } from "../../LanguageProvider";
import {
  isPetCampaignPoi,
  isPetCampaignService,
  PET_CAMPAIGN_CODE,
  PET_CAMPAIGN_MODAL_FOOTER,
  PET_CAMPAIGN_MODAL_TITLE,
  PET_CAMPAIGN_MODAL_VALIDITY
} from "../../utils/petCampaign";

const generateTimestampedID = (originalID) => {
  const randomNum = Math.floor(Math.random() * 1000000);
  const unique6 = String(randomNum).padStart(6, '0');

  return originalID ? unique6 : unique6;
};

const makeExtId = (base) =>
  `${base}${Math.floor(Date.now() / 1000).toString(36)}`;

function luhnCheck(cardNumber) {
  let sum = 0;
  let shouldDouble = false;
  for (let i = cardNumber.length - 1; i >= 0; i--) {
    let digit = parseInt(cardNumber.charAt(i), 10);
    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    shouldDouble = !shouldDouble;
  }
  return sum % 10 === 0;
}

/**
 * 🔹 Invoice datayı oluşturan helper
 * - hediye paketi hariç: unitPrice = subTotal = total = amount
 * - hediye paketi: quantity = packageCount, unitPrice = serviceObj.price,
 * subTotal = total = unitPrice * quantity
 * - billing: serviceObj.billing içinden dolduruluyor
 */
function buildInvoiceData({
  selectedService,
  isExtend,
  requestID,
  serviceObj,
  dailyPrice,
  packageCount,
  amount,
  mallInfo, 
  confirmedAgreementText,
  confirmedAgreementIP,
  paymentID
}) {
  const billing = serviceObj?.billing || {};

  const customerName =
    localStorage.getItem("name") || billing.name || billing.firstName || "";
  const customerLastname =
    localStorage.getItem("lastname") ||
    billing.lastname ||
    billing.lastName ||
    "";

  const customerUID =
    billing.uID ||
    billing.uid ||
    billing.phone ||
    "";

  // kurumsal mı değil mi basit bir kontrol
  const isCorporational =
    !!billing.taxNo && billing.taxNo !== "0" && billing.taxNo !== "0000000000";

  // servis tipine göre ürün kodları vs.
  let stockCode = "";
  let projectTitle = "";
  let projectCode = "";
  let sku = "";
  let productTitleSuffix = "";

  switch (selectedService) {
    case "cocuk":
      stockCode = "CCK001";
      projectTitle = "EASY POİNT-ÇOCUK ARABASI";
      projectCode = "02.04";
      sku = "CCK001";
      productTitleSuffix = "ÇOCUK ARABASI KİRALAMA";
      break;
    case "hayvan":
      stockCode = "PET001";
      projectTitle = "EASY POİNT-PET ARABASI";
      projectCode = "02.05";
      sku = "PET001";
      productTitleSuffix = "EVCİL HAYVAN ARABASI KİRALAMA";
      break;
    case "gift":
      stockCode = "HPK001";
      projectTitle = "EASY POİNT-HEDİYE PAKET";
      projectCode = "02.03";
      sku = "HPK001";
      productTitleSuffix = "HEDİYE PAKETİ SATIN ALMA";
      break;
    case "vestiyer":
      stockCode = "VST001";
      projectTitle = "EASY POİNT - VESTİYER";
      projectCode = "02.13";
      sku = "VST001";
      productTitleSuffix = "VESTİYER HİZMETİ";
      break;
    default:
      stockCode = serviceObj?.stockCode || "";
      projectTitle = serviceObj?.projectTitle || "";
      projectCode = serviceObj?.projectCode || "";
      sku = serviceObj?.sku || stockCode;
      productTitleSuffix = serviceObj?.title || "";
      break;
  }

  const now = new Date();
  const dateStr = now.toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });

  // 🔹 mallInfo'ya göre lokasyon bilgisi
  const mallTitle =
    mallInfo?.mallTitle ||
    mallInfo?.title ||
    billing.title ||
    "TERRACİTY AVM";

  const city = mallInfo?.city || billing.city || "";
  const district = mallInfo?.district || mallInfo?.county || billing.town || billing.region || "";
  const floor = mallInfo?.floor || "";
  const branchCode = mallInfo?.branchCode || "";
  const pointCode =
    mallInfo?.kargoistID ||
    mallInfo?.amazonID ||
    mallInfo?.numeric_index ||
    "";

  const locationParts = [];

  if (city || district) {
    locationParts.push(
      [district, city].filter(Boolean).join(" / ")
    );
  }

  if (floor) {
    locationParts.push(floor);
  }

  if (branchCode) {
    locationParts.push(`Şube: ${branchCode}`);
  }

  if (pointCode) {
    locationParts.push(`Kod: ${pointCode}`);
  }

  const locationMeta = locationParts.join(" - ");

  const titleParts = [
    `Lokasyon: ${mallTitle}`,
    locationMeta || null,
    `Tarih: ${dateStr}`,
    productTitleSuffix || null,
    requestID ? `Id ${requestID}` : null
  ].filter(Boolean);

  const title = titleParts.join(" - ");

  // fiyat alanları
  let quantity = 1;
  let unit = isExtend ? "extend" : "adet";
  let unitPrice = amount;
  let subTotal = amount;
  let total = amount;

  // hediye paketi hariç unitPrice, subTotal, total aynı
  if (selectedService === "gift") {
    const q = packageCount || 1;
    quantity = q;
    unit = "adet";
    unitPrice = serviceObj?.price || amount;
    subTotal = unitPrice * q;
    total = subTotal;
  } else {
    quantity = 1;
    unit = isExtend ? "hour" : "hour";
    unitPrice = amount;
    subTotal = amount;
    total = amount;
  }

  return {
    customer: {
      name: customerName,
      lastname: customerLastname,
      uID: customerUID,
      isCorporational
    },
    order: {
      id: generateTimestampedID(requestID || ""),
      stockCode: stockCode,
      assignedLocation: mallInfo?.accountCode,
      projectLocation: mallInfo?.mallID,
      projectTitle: projectTitle,
      projectCode: projectCode,
      deliveryType: isExtend ? "PickUp Extend" : "PickUp",
      barcode: serviceObj?.barcode || customerUID || "AVM ID",
      quantity: quantity,
      unit: unit,
      unitPrice: unitPrice,
      subTotal: subTotal,
      total: total,
      currency: "TRY",
      sku: sku,
      title: title, // 🔹 artık mallInfo ile dinamik
      taxRate: 20,
      options: {
        size: serviceObj?.options?.size || "M"
      }
    },
    billing: {
      id: generateTimestampedID(requestID || ""),
      street: billing.street || "",
      neighbour: billing.neighbour || "",
      region: billing.town || "",
      city: billing.city || "",
      country: billing.country || "Türkiye",
      phone: "90" + (billing.phone || ""),
      address: billing.address || "",
      title: billing.title || "",
      taxNo: billing.taxNo || "0",
      taxAdministration: billing.taxAdministration || "----",
      email: billing.email || "",
      uID: customerUID,
      paymentID: paymentID
    },
    confirmedAgreementText,
    confirmedAgreementIP
  };
}

function PaymentMethod() {
  const { token, poiID, mallInfo } = useContext(MallContext);
  const { getTranslation } = useLanguage();
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const couponUsedRef = useRef(false);

  const location = useLocation();
  const navigate = useNavigate();
  const {
    selectedService,
    dailyPrice,
    serviceObj,
    conversationID,
    packageCount,
    isExtend = false,
    requestID,
    extendSeconds,
    newWillDeliverDateTimeTs,
    appliedCoupon,
    confirmedAgreementText, // <-- State'den alıyoruz
    confirmedAgreementIP    // <-- State'den alıyoruz
  } = location.state || {};

  // Form fields
  const [cardName, setCardName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiredDate, setExpiredDate] = useState("");
  const [cvv, setCvv] = useState("");  

  // Validation flags
  const [cardNumberError, setCardNumberError] = useState("");
  const [expiredDateError, setExpiredDateError] = useState(false);

  const [isProcessing, setIsProcessing] = useState(false);

  const [mallDetails, setMallDetails] = useState(null);

  const allFieldsAreValid = () => {
    return (
      cardName &&
      !cardNumberError &&
      cardNumber.length >= 12 &&
      expiredDate.length === 5 &&
      !expiredDateError &&
      cvv
    );
  };

  // Payment token
  const [paymentToken, setPaymentToken] = useState("");
  const [iframeSrc, setIframeSrc] = useState(null);
  const [useConversationID, setUseConversationID] = useState(conversationID);
  const [originalConversationID] = useState(conversationID);
  const [retryCount, setRetryCount] = useState(0);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [customerID, setCustomerID] = useState("");
  const [name, setName] = useState("");
  const [lastName, setLastName] = useState("");
  const [mallTitle, setMallTitle] = useState("");
  const shouldShowPetCampaign =
    isPetCampaignPoi(poiID) && isPetCampaignService(selectedService) && !isExtend;
  const getDiscountForProduct = () => {
    if (selectedService === "gift") return "gift";
    if (selectedService === "cocuk" || selectedService === "kid-car") return "kid-car";
    return "pet-car";
  };

  useEffect(() => {
    if (isExtend && conversationID) {
      setUseConversationID(makeExtId(conversationID));
    }
  }, [isExtend, conversationID]);

  useEffect(() => {
    let customerID = localStorage.getItem("customerID");
    let name = localStorage.getItem("name");
    let lastName = localStorage.getItem("lastname");
    let mallTitle = localStorage.getItem("mallTitle");

    setName(name);
    setLastName(lastName);
    setMallTitle(mallTitle);
    setCustomerID(customerID);

    // 1. Payment Token Al
    Services.GetTokenForPayment()
      .then((res) => {
        if (res.data.status) setPaymentToken(res.data.result.token);
      })
      .catch((err) => console.log(err));

    // For accountCode 
    if (token && mallInfo?.mallID) {
      Services.GetMall(token, mallInfo.mallID)
        .then((res) => {
          if (res.data.status) {
            setMallDetails(res.data.result);
          }
        })
        .catch((err) => console.log("GetMall Error:", err));
    }
  }, [token, mallInfo?.mallID]);

  const handleCloseIframe = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIframeSrc(null);
    setIsProcessing(false);

    const newCount = retryCount + 1;
    setRetryCount(newCount);

    if (isExtend) {
      const base = useConversationID.replace(/-r\d+$/, "");
      setUseConversationID(makeExtId(base));
    } else {
      setUseConversationID(`${originalConversationID}-${newCount}`);
    }
  };

  const handleCardNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setCardNumber(value);
    if (value.length >= 12 && !luhnCheck(value)) {
      setCardNumberError(getTranslation("invalidCardNumber"));
    } else {
      setCardNumberError("");
    }
  };

  const handleExpiryDateChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length >= 2) {
      let month = value.slice(0, 2);
      if (parseInt(month, 10) > 12) month = "12";
      value = month + (value.length > 2 ? `/${value.slice(2, 4)}` : "");
    }
    setExpiredDate(value);

    if (value.length === 5) {
      const [mStr, yStr] = value.split("/");
      const m = parseInt(mStr, 10);
      const y = 2000 + parseInt(yStr, 10);
      const today = new Date();
      const isExpired =
        y < today.getFullYear() ||
        (y === today.getFullYear() && m < today.getMonth() + 1);
      setExpiredDateError(isExpired);
    } else {
      setExpiredDateError(false);
    }
  };

  // 🔹 Kuponu işaretlemek için helper
  const markCouponUsedIfNeeded = async () => {
    if (couponUsedRef.current) return;
    if (!appliedCoupon || !appliedCoupon.code) return;

    try {
      const discountForProduct = getDiscountForProduct();
      const usedCustomerID = customerID || localStorage.getItem("customerID") || "";
      const usedName = name || localStorage.getItem("name") || "";
      const usedLastName = lastName || localStorage.getItem("lastname") || "";
      const usedMallTitle = mallTitle || mallInfo?.mallTitle || localStorage.getItem("mallTitle") || "";
      const usedRequestID = requestID || originalConversationID || conversationID || "";
      const transactionAmount = Number(appliedCoupon?.finalPrice || 0) + Number(appliedCoupon?.discountAmount || 0);
      const netAmount = Number(appliedCoupon?.finalPrice ?? dailyPrice ?? 0);

      const res = await Services.UseCoupon(
        token,
        appliedCoupon.code,
        discountForProduct,
        usedCustomerID,
        usedName,
        usedLastName,
        usedMallTitle,
        usedRequestID,
        transactionAmount,
        netAmount
      );
      couponUsedRef.current = true;
    } catch (err) {
      console.log("UseCoupon error:", err);
      // hata olsa bile ödemeyi success sayıyoruz
    }
  };

  const handleListenPaymentResult = () => {
    let prefix = "";
    if (selectedService === "cocuk") prefix = "CCK-";
    else if (selectedService === "hayvan") prefix = "PET-";
    else if (selectedService === "gift") prefix = "HPK-";

    let cid = prefix + useConversationID;

    Services.ListenPayment(cid)
      .then((res) => {
        const newCount = retryCount + 1;

        if (res.data.status && res.data.result === "1") {
          if (intervalRef.current) clearInterval(intervalRef.current);
          setIframeSrc(null);

          Services.SetPaid(
            originalConversationID,
            cid,
            cardNumber.slice(-4)
          )
            .then(async (r) => {
              if (!r.data.status) {
                setShowErrorModal(true);
                setErrorMessage(getTranslation("paymentSuccessWithIssue"));
                setRetryCount(newCount);
                setUseConversationID(`${originalConversationID}-${newCount}`);
                return;
              }

              // ✅ Ödeme başarılı -> Eğer extend ise şimdi uzat
              if (isExtend) {
                try {
                  const extPrice = serviceObj?.price ?? dailyPrice;
                  const currency = "TL";

                  const extRes = await Services.ExtendOrderDeliverTime(
                    token,
                    requestID,
                    extendSeconds,
                    extPrice,
                    currency
                  );

                  if (!extRes.data?.status) {
                    console.log("extend err:", extRes.data);
                    setShowErrorModal(true);
                    setErrorMessage(
                      getTranslation("extendFailedAfterPayment") ||
                      "Ödeme alındı ancak süre uzatma başarısız oldu. Lütfen destek ile iletişime geçin."
                    );
                    return;
                  }
                } catch (err) {
                  setShowErrorModal(true);
                  setErrorMessage(
                    getTranslation("extendFailedAfterPayment") ||
                    "Ödeme alındı ancak süre uzatma sırasında bir hata oluştu."
                  );
                  return;
                }
              }

              // 🔹 Kupon kullanımı (varsa)
              await markCouponUsedIfNeeded();

              try {
                // ödenen tutarı belirle
                let paidAmount;
                if (isExtend) {
                  // extend için: extPrice
                  paidAmount = serviceObj?.price ?? dailyPrice;
                } else if (selectedService === "gift") {
                  // hediye paketi: paket adedi * birim fiyat
                  paidAmount = (serviceObj?.price || 0) * (packageCount || 1);
                } else {
                  // diğerleri: serviceObj.price
                  paidAmount = serviceObj?.price ?? dailyPrice;
                }

                const finalMallInfo = { ...mallInfo, ...mallDetails };

                const invoiceData = buildInvoiceData({
                  selectedService,
                  isExtend,
                  requestID,
                  serviceObj,
                  dailyPrice,
                  packageCount,
                  amount: paidAmount,
                  mallInfo: finalMallInfo,
                  confirmedAgreementText,
                  confirmedAgreementIP,
                  paymentID: cid   
                });

                // 🔹 1) EP token al
                let epToken = null;
                try {
                  const epRes = await Services.GetEPToken();
                  if (epRes?.data?.status) {
                    // GetTokenForPayment ile aynı pattern varsayımı:
                    epToken =
                      epRes.data.result?.token ||
                      epRes.data.token ||
                      null;
                  } else {
                    console.log("GetEPToken failed:", epRes?.data);
                  }
                } catch (epErr) {
                  console.log("GetEPToken error:", epErr);
                }

                // EP token alınmadıysa fatura denemesini atla, akışı bozma
                if (!epToken) {
                  console.log(
                    "EP token alınamadı, InvoiceAdd çağrılmayacak."
                  );
                } else {
                  const res = await Services.InvoiceAdd(
                      epToken, 
                      invoiceData.customer, 
                      invoiceData.billing, 
                      invoiceData.order,
                      invoiceData.confirmedAgreementText,
                      invoiceData.confirmedAgreementIP  
                  );
                }
              } catch (invErr) {
                console.log("InvoiceAdd error:", invErr);
              }

              setShowSuccessModal(true);
            })
            .catch(() => {
              setShowErrorModal(true);
              setErrorMessage(getTranslation("paymentFailed"));
            })
            .finally(() => {
              setIsProcessing(false);
            });
        } else if (res.data.status && res.data.result === "2") {
          if (intervalRef.current) clearInterval(intervalRef.current);
          setIframeSrc(null);
          setShowErrorModal(true);
          Services.GetPaymentError(cid)
            .then((r) => {
              setErrorMessage(
                r.data.result.errors?.errorDescription ||
                getTranslation("paymentFailed")
              );
              setIsProcessing(false);
            })
            .catch(() => {
              setIsProcessing(false);
            });
          setRetryCount(newCount);
          setUseConversationID(`${originalConversationID}-${newCount}`);
        }
      })
      .catch(() => {
        setIsProcessing(false);
      });
  };

  const handleCompletePayment = () => {
    if (isProcessing) return;
    setIsProcessing(true);
    let prefix = "";
    if (selectedService === "cocuk") prefix = "CCK-";
    else if (selectedService === "hayvan") prefix = "PET-";
    else if (selectedService === "gift") prefix = "HPK-";

    let price = serviceObj.price;
    if (selectedService === "gift") {
      price = serviceObj.price * packageCount;
    }
    let convId = prefix + useConversationID;

    Services.PaymentWith3D(
      convId,
      price,
      localStorage.getItem("name"),
      localStorage.getItem("lastname"),
      serviceObj.billing.phone,
      cardName,
      cardNumber,
      expiredDate.slice(-2),
      expiredDate.slice(0, 2),
      cvv
    )
      .then((res) => {
        if (res.data.status) {
          const blob = new Blob([res.data.result], { type: "text/html" });
          setIframeSrc(URL.createObjectURL(blob));
          intervalRef.current = setInterval(handleListenPaymentResult, 3000);

          timeoutRef.current = setTimeout(() => {
            handleCloseIframe();
          }, 150000);
        } else {
          setShowErrorModal(true);
          setErrorMessage(getTranslation("paymentFailed"));
          const newCount = retryCount + 1;
          setRetryCount(newCount);
          setUseConversationID(`${originalConversationID}-${newCount}`);
          setIsProcessing(false);
        }
      })
      .catch(() => {
        setIsProcessing(false);
      });
  };

  return (
    <div className="payment-method-container">
      <div className="header-wrapper">
        <span
          onClick={() =>
            navigate("/rent-confirmation", {
              state: {
                selectedService,
                dailyPrice,
                packageCount,
                type: isExtend ? "extend" : "new",
                requestID,
                originalWillDeliverDateTime: newWillDeliverDateTimeTs
                  ? newWillDeliverDateTimeTs - extendSeconds
                  : undefined,
                selectedDurationKey: undefined
              }
            })
          }
          className="general-back-button"
        >
          <TbArrowLeft color="rgba(71, 84, 103, 1)" />
        </span>
      </div>

      <div className="payment-method-header">
        <span className="payment-method-title">
          {getTranslation("paymentMethod")}
        </span>
        <span className="payment-method-subtitle">
          {getTranslation("enterPaymentInfo")}
        </span>
      </div>

      <div className="card-preview">
        <CreditCard
          cardNumber={cardNumber}
          cardHolder={cardName}
          expiryDate={expiredDate}
        />
      </div>

      <div className="payment-method-form">
        <InputField
          value={cardName}
          onChange={(e) => setCardName(e.target.value)}
          label={getTranslation("cardholderName")}
          placeholder={getTranslation("cardholderName")}
        />

        <InputField
          value={cardNumber}
          onChange={handleCardNumberChange}
          label={getTranslation("cardNumber")}
          placeholder={getTranslation("cardNumber")}
        />
        {cardNumberError && (
          <span className="validation-error">{cardNumberError}</span>
        )}

        <div
          style={{
            width: "100%",
            alignItems: "center",
            flexDirection: "row",
            display: "flex",
            gap: "24px"
          }}
        >
          <InputField
            value={expiredDate}
            maxLength={5}
            style={{ width: "100%" }}
            onChange={handleExpiryDateChange}
            label={getTranslation("expiryDate")}
            placeholder={getTranslation("expiryPlaceholder")}
          />
          <InputField
            type="password"
            style={{ width: "100%" }}
            value={cvv}
            onChange={(e) => setCvv(e.target.value.replace(/\D/g, ""))}
            label={getTranslation("cvv")}
            placeholder={getTranslation("cvv")}
          />
        </div>
        {expiredDateError && (
          <span className="validation-error">
            {getTranslation("invalidExpiryDate")}
          </span>
        )}

        <Button
          disabled={!allFieldsAreValid() || isProcessing}
          onClick={handleCompletePayment}
          icon={<TbCreditCard size={22} />}
          label={getTranslation("completePayment")}
          style={{ width: "100%", marginTop: "18px" }}
        />
      </div>

      {isProcessing && !iframeSrc && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            backgroundColor: "rgba(17, 24, 39, 0.45)",
            zIndex: 10001
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "14px",
              padding: "20px 24px",
              minWidth: "320px",
              maxWidth: "90vw",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "10px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.18)"
            }}
          >
            <div
              style={{
                width: "52px",
                height: "52px",
                borderRadius: "999px",
                backgroundColor: "#FFFAEB",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <TbAlertTriangle size={28} color="#F79009" />
            </div>
            <div className="btn-spinner" />
            <span style={{ color: "#344054", fontSize: "14px", textAlign: "center", fontWeight: 600 }}>
              {getTranslation("paymentProcessingDoNotClose") || "İşleminiz devam ediyor. Lütfen sayfayı kapatmayın."}
            </span>
          </div>
        </div>
      )}

      {iframeSrc && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: 9999,
            width: "100vw",
            height: "100vh",
            backgroundColor: "#fff"
          }}
        >
          <button
            onClick={handleCloseIframe}
            style={{
              position: "absolute",
              top: 16,
              right: 16,
              background: "transparent",
              border: "none",
              fontSize: "28px",
              cursor: "pointer",
              zIndex: 10000
            }}
            aria-label="Close"
          >
            ✕
          </button>

          <iframe
            src={iframeSrc}
            title="3D Secure"
            style={{ width: "100%", height: "100%", border: "none" }}
          />
        </div>
      )}

      {showSuccessModal && (
        <SuccessModal
          campaign={{
            isVisible: shouldShowPetCampaign,
            code: PET_CAMPAIGN_CODE,
            title: PET_CAMPAIGN_MODAL_TITLE,
            validity: PET_CAMPAIGN_MODAL_VALIDITY,
            footer: PET_CAMPAIGN_MODAL_FOOTER
          }}
          onClose={() => {
            setShowSuccessModal(false);
            navigate("/mainpage");
          }}
        />
      )}
      {showErrorModal && (
        <ErrorModal
          message={errorMessage}
          onClose={() => setShowErrorModal(false)}
        />
      )}
    </div>
  );
}

export default PaymentMethod;
